
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Globe, Users, Handshake, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function CulturalHumanCentricHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-[#FDF8F0] text-[#4A4A4A] font-sans">
      <header className="container mx-auto px-6 py-5 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-[#D47A45]">Common Ground</h2>
        <nav className="hidden md:flex gap-8 items-center text-sm">
          <a href="#" className="hover:text-[#D47A45]">
            Our Stories
          </a>
          <a href="#" className="hover:text-[#D47A45]">
            Our Mission
          </a>
          <a href="#" className="hover:text-[#D47A45]">
            Get Involved
          </a>
          <Button className="bg-[#D47A45] text-white hover:bg-[#B96A3C] rounded-full">
            Donate
          </Button>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="md:hidden bg-[#FDF8F0]/95 border-y">
          <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg">
            <a href="#" className="hover:text-[#D47A45]">Our Stories</a>
            <a href="#" className="hover:text-[#D47A45]">Our Mission</a>
            <a href="#" className="hover:text-[#D47A45]">Get Involved</a>
            <Button className="bg-[#D47A45] text-white hover:bg-[#B96A3C] rounded-full w-full mt-2">
              Donate
            </Button>
          </nav>
        </div>
      )}

      <main>
        <section className="py-20 md:py-32">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-7xl font-serif text-[#333] leading-tight">
              Weaving threads of <br /> human connection.
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-lg">
              We are a non-profit organization dedicated to fostering
              cross-cultural understanding and celebrating the rich tapestry of
              global heritage.
            </p>
            <div className="mt-12 relative w-full max-w-5xl mx-auto h-[30vh] md:h-[60vh]">
              <Image
                src="https://picsum.photos/seed/culture1/1200/800"
                alt="A diverse group of people from different cultures smiling and interacting"
                layout="fill"
                objectFit="cover"
                className="rounded-xl shadow-lg"
                data-ai-hint="diverse people"
              />
            </div>
          </div>
        </section>

        <section className="bg-white py-24 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-serif text-[#333] mb-4">
                Our Approach
              </h2>
              <p className="text-lg">
                Through shared experiences, storytelling, and collaborative
                projects, we build bridges between communities.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <Globe className="w-12 h-12 text-[#D47A45] mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Cultural Exchange</h3>
                <p>
                  Immersive programs that connect individuals from different
                  backgrounds.
                </p>
              </div>
              <div>
                <Users className="w-12 h-12 text-[#D47A45] mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">
                  Community Storytelling
                </h3>
                <p>
                  A platform for sharing personal histories and preserving oral
                  traditions.
                </p>
              </div>
              <div>
                <Handshake className="w-12 h-12 text-[#D47A45] mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Collaborative Arts</h3>
                <p>
                  Projects that bring people together to create art that
                  reflects shared values.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-serif text-[#333] mb-4">
                Voices from Our Community
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-[#FFFBF5] border-[#F0E6D8]">
                <CardContent className="p-8">
                  <p className="text-lg italic mb-6">
                    "Common Ground opened my eyes to a world of perspectives I
                    never knew existed. It was a transformative experience."
                  </p>
                  <div className="flex items-center gap-4">
                    <Image
                      src="https://picsum.photos/seed/person1/100/100"
                      width={48}
                      height={48}
                      className="rounded-full"
                      data-ai-hint="portrait face"
                      alt="Portrait of a smiling woman"
                    />
                    <div>
                      <p className="font-bold">Amina K.</p>
                      <p className="text-sm text-stone-500">
                        Program Participant, Morocco
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#FFFBF5] border-[#F0E6D8]">
                <CardContent className="p-8">
                  <p className="text-lg italic mb-6">
                    "Seeing our community's stories preserved and shared with
                    the world has been incredibly empowering."
                  </p>
                  <div className="flex items-center gap-4">
                    <Image
                      src="https://picsum.photos/seed/person2/100/100"
                      width={48}
                      height={48}
                      className="rounded-full"
                      data-ai-hint="portrait man"
                      alt="Portrait of a smiling man"
                    />
                    <div>
                      <p className="font-bold">Carlos R.</p>
                      <p className="text-sm text-stone-500">
                        Community Leader, Colombia
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-[#4A4A4A] text-white py-16 px-6">
        <div className="container mx-auto text-center">
          <h3 className="text-3xl font-serif mb-4">Join Our Global Family</h3>
          <p className="max-w-xl mx-auto mb-8 text-gray-300">
            Support our mission to create a more connected and compassionate
            world.
          </p>
          <Button
            size="lg"
            className="bg-[#D47A45] text-white hover:bg-[#B96A3C] rounded-full"
          >
            Become a Supporter
          </Button>
          <div className="mt-16 pt-8 border-t border-gray-600 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400 gap-4">
            <p>&copy; {new Date().getFullYear()} Common Ground Foundation</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white">
                Contact
              </a>
              <a href="#" className="hover:text-white">
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
